import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { 
  Package, 
  Truck, 
  LogOut, 
  Menu,
  Flame,
  Calculator,
  StickyNote,
  LayoutDashboard,
  BookOpen,
  TrendingUp,
  Users,
  Scale,
  Activity,
  AlertTriangle,
  Upload,
  BarChart3,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Printer,
  HelpCircle,
  MessageSquare,
  Receipt,
  X,
  MoreHorizontal,
  ChevronRight,
  Settings,
  Database,
  DollarSign,
  HeartPulse,
  Star,
  ScrollText,
  KeyRound,
  Eye,
  EyeOff,
  Settings as SettingsIcon
} from "lucide-react";
import { useState, useEffect, useCallback, useRef } from "react";
import useActivityTracking from "@/hooks/useActivityTracking";
import { toast } from "sonner";
import PrintSettingsDialog from "@/components/PrintSettingsDialog";
import { InstallAppPrompt } from "@/components/InstallAppPrompt";
import GlobalSearch from "@/components/GlobalSearch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_VISIBLE_NAV = [
  "/favorites", "/products", "/shipping", "/costing", "/ledger", "/sales",
  "/ledger-reconciliation", "/financial-overview", "/errors", "/upload-center", "/payment-verification"
];

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false); // Default closed on mobile
  const [showAllNav, setShowAllNav] = useState(() => localStorage.getItem("eximflow_show_all_nav") === "1");
  const [navPrefsOpen, setNavPrefsOpen] = useState(false);
  const [visibleNavPaths, setVisibleNavPaths] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("eximflow_visible_nav") || "null");
      if (Array.isArray(saved) && saved.length) return saved;
    } catch {}
    return DEFAULT_VISIBLE_NAV;
  });
  const [printSettingsOpen, setPrintSettingsOpen] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  
  // Sync status state
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  
  // Touch/swipe handling for mobile sidebar
  const sidebarRef = useRef(null);
  const touchStartX = useRef(0);
  const touchCurrentX = useRef(0);
  
  // Initialize activity tracking (auto-tracks page views)
  useActivityTracking();
  
  // Close sidebar when route changes (mobile)
  useEffect(() => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
      setMobileMoreOpen(false);
    }
  }, [location.pathname]);
  
  // Handle touch events for swipe-to-close sidebar
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  
  const handleTouchMove = (e) => {
    touchCurrentX.current = e.touches[0].clientX;
  };
  
  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchCurrentX.current;
    // Swipe left to close (when sidebar is open)
    if (diff > 50 && sidebarOpen) {
      setSidebarOpen(false);
    }
  };
  
  // Handle edge swipe to open sidebar
  useEffect(() => {
    const handleEdgeSwipe = (e) => {
      if (e.touches[0].clientX < 20 && !sidebarOpen) {
        touchStartX.current = e.touches[0].clientX;
      }
    };
    
    const handleEdgeSwipeEnd = (e) => {
      if (touchStartX.current < 20 && e.changedTouches[0].clientX > 100) {
        setSidebarOpen(true);
      }
    };
    
    document.addEventListener('touchstart', handleEdgeSwipe);
    document.addEventListener('touchend', handleEdgeSwipeEnd);
    
    return () => {
      document.removeEventListener('touchstart', handleEdgeSwipe);
      document.removeEventListener('touchend', handleEdgeSwipeEnd);
    };
  }, [sidebarOpen]);

  // Fetch sync status
  const fetchSyncStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      
      const response = await fetch(`${BACKEND_URL}/api/sync/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSyncStatus(data);
      }
    } catch (error) {
      console.error("Failed to fetch sync status:", error);
    }
  }, []);

  // Background auto-sync (lightweight - only when needed)
  const runBackgroundSync = useCallback(async () => {
    // Only run for admins
    if (user?.role !== "super_admin") return;
    
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      
      // First check status
      const statusResponse = await fetch(`${BACKEND_URL}/api/sync/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!statusResponse.ok) return;
      
      const status = await statusResponse.json();
      setSyncStatus(status);
      
      // Only sync if there are mismatches
      if (status.sync_health !== "good" && status.sampled_mismatches > 0) {
        setSyncing(true);
        
        // Run lightweight sync in background
        const syncResponse = await fetch(`${BACKEND_URL}/api/sync/bulk`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ sync_type: "sales" }) // Only sync sales for speed
        });
        
        if (syncResponse.ok) {
          const result = await syncResponse.json();
          if (result.sales_updated > 0) {
            toast.success(`Auto-synced ${result.sales_updated} records`, { duration: 3000 });
          }
          setLastSyncTime(new Date());
          // Refresh status
          fetchSyncStatus();
        }
        
        setSyncing(false);
      }
    } catch (error) {
      console.error("Background sync failed:", error);
      setSyncing(false);
    }
  }, [user?.role, fetchSyncStatus]);

  // Manual sync trigger
  const handleManualSync = async () => {
    if (syncing) return;
    
    setSyncing(true);
    try {
      const token = localStorage.getItem("token");
      
      const response = await fetch(`${BACKEND_URL}/api/sync/bulk`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ sync_type: "all" })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`Synced: ${result.sales_updated} sales, ${result.ledger_updated} ledger`);
        setLastSyncTime(new Date());
        fetchSyncStatus();
      } else {
        toast.error("Sync failed");
      }
    } catch (error) {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  // Initial fetch and periodic background sync
  useEffect(() => {
    fetchSyncStatus();
    
    // Fetch sync status every 2 minutes for real-time indicator updates
    const statusInterval = setInterval(() => {
      fetchSyncStatus();
    }, 120000); // 2 minutes
    
    // Run background sync every 10 minutes - lightweight auto-sync
    const syncInterval = setInterval(() => {
      runBackgroundSync();
    }, 600000); // 10 minutes
    
    return () => {
      clearInterval(statusInterval);
      clearInterval(syncInterval);
    };
  }, [fetchSyncStatus, runBackgroundSync]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Module to nav item mapping
  const moduleToNavItem = {
    dashboard: { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
    crm: { path: "/crm", label: "CRM Hub", icon: Users },
    products: { path: "/products", label: "Product Prices", icon: Package },
    shipping: { path: "/shipping", label: "Shipping Prices", icon: Truck },
    costing: { path: "/costing", label: "Shipment Costing", icon: Calculator },
    shipment_costing: { path: "/costing", label: "Shipment Costing", icon: Calculator },
    ledger: { path: "/ledger", label: "Ledger (Costing)", icon: BookOpen },
    sales: { path: "/sales", label: "Sales & Profit", icon: TrendingUp },
    sales_profit: { path: "/sales", label: "Sales & Profit", icon: TrendingUp },
    ledger_reconciliation: { path: "/ledger-reconciliation", label: "Ledger Reconciliation", icon: Scale },
    financial_overview: { path: "/financial-overview", label: "Financial Overview", icon: DollarSign },
    reports: { path: "/reports", label: "Reports", icon: BarChart3 },
    notes: { path: "/notes", label: "Notepad", icon: StickyNote },
    error_resolution: { path: "/errors", label: "Error Resolution", icon: AlertTriangle },
    upload_center: { path: "/upload-center", label: "Upload Center", icon: Upload },
    upload: { path: "/upload-center", label: "Upload Center", icon: Upload },
    payment_verification: { path: "/payment-verification", label: "Payment Verification", icon: Receipt },
    documents: { path: "/documents", label: "Documents", icon: ScrollText },
  };

  // Helper to check if user has access to a module
  const hasModuleAccess = (moduleKey) => {
    if (user?.role === "super_admin") return true;
    const modules = user?.allowed_modules || [];
    return modules.includes(moduleKey) || modules.includes("all");
  };

  // Build nav items based on role and allowed_modules
  const buildNavItems = () => {
    // Super admin gets everything
    if (user?.role === "super_admin") {
      return [
        { path: "/favorites", label: "Favorites", icon: Star },
        moduleToNavItem.dashboard,
        moduleToNavItem.crm,
        moduleToNavItem.products,
        moduleToNavItem.shipping,
        moduleToNavItem.costing,
        moduleToNavItem.ledger,
        moduleToNavItem.sales,
        moduleToNavItem.ledger_reconciliation,
        moduleToNavItem.financial_overview,
        moduleToNavItem.reports,
        moduleToNavItem.notes,
        { path: "/users", label: "User Management", icon: Users },
        moduleToNavItem.error_resolution,
        moduleToNavItem.upload_center,
        moduleToNavItem.payment_verification,
        moduleToNavItem.documents,
        { path: "/vault", label: "Password Vault", icon: KeyRound },
        { path: "/data-management", label: "Data Management", icon: Database },
        { path: "/health", label: "System Health", icon: HeartPulse },
        { path: "/logs", label: "Activity Logs", icon: Activity },
        { path: "/support", label: "Support Center", icon: MessageSquare },
        { path: "/help", label: "Help Center", icon: HelpCircle }
      ];
    }
    
    // For other roles, build based on allowed_modules
    const userModules = user?.allowed_modules || [];
    const items = [];
    
    // Add modules based on user's allowed_modules array
    const moduleOrder = [
      'dashboard', 'crm', 'products', 'shipping', 'costing', 'shipment_costing',
      'ledger', 'sales', 'sales_profit', 'ledger_reconciliation', 'financial_overview', 'reports', 
      'notes', 'error_resolution', 'upload_center', 'upload', 'payment_verification', 'documents'
    ];
    
    const addedPaths = new Set();
    
    for (const moduleKey of moduleOrder) {
      if (userModules.includes(moduleKey) && moduleToNavItem[moduleKey]) {
        const navItem = moduleToNavItem[moduleKey];
        // Avoid duplicates (e.g., 'upload' and 'upload_center' map to same path)
        if (!addedPaths.has(navItem.path)) {
          items.push(navItem);
          addedPaths.add(navItem.path);
        }
      }
    }
    
    // Always add support and help for non-admin roles
    items.push({ path: "/support", label: "Support", icon: MessageSquare });
    items.push({ path: "/help", label: "Help & FAQ", icon: HelpCircle });
    
    return items;
  };
  
  const navItemsAll = buildNavItems();

  // Panels visible when eye toggle is off — user self-manages via the settings dialog
  const visibleSet = new Set(visibleNavPaths);
  const navItems = showAllNav ? navItemsAll : navItemsAll.filter((i) => visibleSet.has(i.path));

  const toggleNavPath = (path) => {
    setVisibleNavPaths((prev) => {
      const next = prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path];
      localStorage.setItem("eximflow_visible_nav", JSON.stringify(next));
      return next;
    });
  };

  const resetNavPrefs = () => {
    setVisibleNavPaths(DEFAULT_VISIBLE_NAV);
    localStorage.setItem("eximflow_visible_nav", JSON.stringify(DEFAULT_VISIBLE_NAV));
  };

  const toggleShowAllNav = () => {
    setShowAllNav((prev) => {
      localStorage.setItem("eximflow_show_all_nav", prev ? "0" : "1");
      return !prev;
    });
  };

  // Get role display text
  const getRoleDisplay = (role) => {
    switch (role) {
      case "super_admin": return "Super Admin";
      case "partner": return "Partner";
      case "agent": return "Agent";
      case "error_manager": return "Error Manager";
      case "uploader": return "Upload Team";
      default: return role;
    }
  };

  // Mobile bottom navigation items (key actions based on allowed modules)
  const getMobileNavItems = () => {
    const items = [];
    const userModules = user?.allowed_modules || [];
    
    // For super_admin, show default set
    if (user?.role === "super_admin") {
      return [
        { path: "/dashboard", label: "Home", icon: LayoutDashboard },
        { path: "/ledger-reconciliation", label: "Ledger", icon: Scale },
        { path: "/financial-overview", label: "Finance", icon: DollarSign },
        { path: "/upload-center", label: "Upload", icon: Upload },
        { path: "/payment-verification", label: "Payments", icon: Receipt },
      ];
    }
    
    // Build mobile nav based on allowed modules (max 4 items)
    const priorityModules = [
      { key: 'error_resolution', item: { path: "/errors", label: "Errors", icon: AlertTriangle } },
      { key: 'upload_center', item: { path: "/upload-center", label: "Upload", icon: Upload } },
      { key: 'upload', item: { path: "/upload-center", label: "Upload", icon: Upload } },
      { key: 'payment_verification', item: { path: "/payment-verification", label: "Payments", icon: Receipt } },
      { key: 'dashboard', item: { path: "/dashboard", label: "Home", icon: LayoutDashboard } },
      { key: 'products', item: { path: "/products", label: "Products", icon: Package } },
      { key: 'sales', item: { path: "/sales", label: "Sales", icon: TrendingUp } },
      { key: 'sales_profit', item: { path: "/sales", label: "Sales", icon: TrendingUp } },
      { key: 'reports', item: { path: "/reports", label: "Reports", icon: BarChart3 } },
    ];
    
    const addedPaths = new Set();
    for (const { key, item } of priorityModules) {
      if (userModules.includes(key) && !addedPaths.has(item.path) && items.length < 3) {
        items.push(item);
        addedPaths.add(item.path);
      }
    }
    
    // Always add help as the last item
    items.push({ path: "/help", label: "Help", icon: HelpCircle });
    
    return items;
  };
  
  const mobileNavItems = getMobileNavItems();

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar - Desktop: static, Mobile: slide-over drawer */}
      <aside 
        ref={sidebarRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className={`
          fixed inset-y-0 left-0 z-50 
          bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 border-r border-slate-800
          transform transition-transform duration-300 ease-in-out
          w-72 lg:w-64 lg:static lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="h-screen flex flex-col overflow-hidden">
          {/* Logo with close button for mobile */}
          <div className="p-4 lg:p-5 border-b border-slate-800 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* Flame Icon */}
                <div className="relative w-10 h-10 flex items-center justify-center">
                  <Flame className="w-8 h-8 text-orange-500" style={{ filter: 'drop-shadow(0 0 8px rgba(249, 115, 22, 0.5))' }} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-4 h-4 rounded-full bg-gradient-to-b from-orange-400 via-emerald-400 to-blue-400 opacity-60 blur-sm"></div>
                  </div>
                </div>
                <span className="font-heading text-xl font-bold text-white">EximFlow</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleShowAllNav}
                  title={showAllNav ? "Show core panels only" : "Show all panels"}
                  className="text-slate-400 hover:text-white hover:bg-slate-800 h-8 w-8"
                  data-testid="nav-show-all-toggle"
                >
                  {showAllNav ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setNavPrefsOpen(true)}
                  title="Choose which panels to show"
                  className="text-slate-400 hover:text-white hover:bg-slate-800 h-8 w-8 -ml-1"
                  data-testid="nav-settings-btn"
                >
                  <SettingsIcon className="w-4 h-4" />
                </Button>
              </div>
              {/* Close button - visible only on mobile */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden text-slate-400 hover:text-white hover:bg-slate-800 h-10 w-10"
                data-testid="sidebar-close-btn"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Navigation - Scrollable with touch-friendly sizing */}
          <nav className="flex-1 p-3 lg:p-4 space-y-1 lg:space-y-2 overflow-y-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => window.innerWidth < 1024 && setSidebarOpen(false)}
                className={({ isActive }) => `
                  flex items-center gap-3 px-4 py-3.5 lg:py-3 rounded-lg transition-all
                  min-h-[48px] touch-manipulation
                  ${isActive 
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/25' 
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white active:bg-slate-700'}
                `}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium text-sm lg:text-base">{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Sync Status Indicator - For admins and error managers */}
          {(user?.role === "super_admin" || user?.role === "error_manager") && (
            <div className="px-3 lg:px-4 py-3 border-t border-slate-800 flex-shrink-0 hidden lg:block">
              {/* Sync Health Header */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Sync Health</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleManualSync}
                  disabled={syncing}
                  className="h-6 px-2 text-xs text-slate-400 hover:text-white hover:bg-slate-800"
                  title="Force sync all data now"
                >
                  <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              
              {/* Status Badge */}
              <div 
                className={`
                  flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer
                  transition-all duration-200
                  ${syncing 
                    ? 'bg-blue-500/10 border border-blue-500/30' 
                    : syncStatus?.sync_health === "good" 
                      ? 'bg-green-500/10 border border-green-500/30 hover:bg-green-500/20' 
                      : syncStatus?.sync_health === "critical"
                        ? 'bg-red-500/10 border border-red-500/30 hover:bg-red-500/20'
                        : 'bg-yellow-500/10 border border-yellow-500/30 hover:bg-yellow-500/20'
                  }
                `}
                onClick={!syncing ? handleManualSync : undefined}
                title={syncing ? "Syncing in progress..." : "Click to sync now"}
              >
                {syncing ? (
                  <RefreshCw className="w-4 h-4 text-blue-400 animate-spin flex-shrink-0" />
                ) : syncStatus?.sync_health === "good" ? (
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                ) : syncStatus?.sync_health === "critical" ? (
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium ${
                    syncing ? 'text-blue-300' :
                    syncStatus?.sync_health === "good" ? 'text-green-300' :
                    syncStatus?.sync_health === "critical" ? 'text-red-300' :
                    'text-yellow-300'
                  }`}>
                    {syncing ? "Syncing..." : 
                     syncStatus?.sync_health === "good" ? "All Synced" :
                     syncStatus?.sync_health === "critical" ? "Sync Critical" :
                     "Needs Sync"}
                  </p>
                  <p className="text-xs text-slate-500">
                    {syncing ? "Please wait..." :
                     syncStatus?.sync_health === "good" 
                       ? "Data is up to date"
                       : `${syncStatus?.sampled_mismatches || 0} records pending`
                    }
                  </p>
                </div>
              </div>
              
              {/* Last Sync Time */}
              {lastSyncTime && (
                <p className="text-xs text-slate-600 mt-2 text-center">
                  Last synced: {lastSyncTime.toLocaleTimeString()}
                </p>
              )}
              
              {/* Quick Stats (collapsed by default, show on hover) */}
              {syncStatus && !syncing && (
                <div className="mt-2 grid grid-cols-2 gap-1 text-xs">
                  <div className="bg-slate-800/50 rounded px-2 py-1">
                    <span className="text-slate-500">Parcels:</span>
                    <span className="text-slate-300 ml-1">{syncStatus.total_orders?.toLocaleString()}</span>
                  </div>
                  <div className="bg-slate-800/50 rounded px-2 py-1">
                    <span className="text-slate-500">Sales:</span>
                    <span className="text-slate-300 ml-1">{syncStatus.total_sales?.toLocaleString()}</span>
                    {syncStatus.total_orders && syncStatus.total_sales && syncStatus.total_orders !== syncStatus.total_sales && (
                      <span className={`ml-1 ${syncStatus.total_sales > syncStatus.total_orders ? 'text-green-400' : 'text-yellow-400'}`}>
                        ({syncStatus.total_sales > syncStatus.total_orders ? '+' : ''}{(syncStatus.total_sales - syncStatus.total_orders).toLocaleString()})
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* User section */}
          <div className="p-3 lg:p-4 border-t border-slate-800 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 lg:w-9 lg:h-9 rounded-full bg-gradient-to-br from-orange-500 via-emerald-500 to-blue-500 flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">
                    {user?.username?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-white">{user?.display_name || user?.username}</p>
                  <p className="text-xs text-slate-500">{getRoleDisplay(user?.role)}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setPrintSettingsOpen(true)}
                  title="Print Settings"
                  data-testid="print-settings-btn"
                  className="text-slate-400 hover:text-white hover:bg-slate-800 h-10 w-10 lg:h-9 lg:w-9"
                >
                  <Printer className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleLogout}
                  data-testid="logout-btn"
                  className="text-slate-400 hover:text-white hover:bg-slate-800 h-10 w-10 lg:h-9 lg:w-9"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Print Settings Dialog */}
      <Dialog open={navPrefsOpen} onOpenChange={setNavPrefsOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="nav-prefs-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <SettingsIcon className="w-4 h-4" /> Menu Panels
            </DialogTitle>
            <DialogDescription>
              Tick the panels you want visible in the side menu. Use the eye button to temporarily show everything.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            {navItemsAll.map((item) => (
              <label
                key={item.path}
                className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-muted cursor-pointer"
                data-testid={`nav-pref-${item.path.replace("/", "")}`}
              >
                <Checkbox
                  checked={visibleSet.has(item.path)}
                  onCheckedChange={() => toggleNavPath(item.path)}
                />
                <item.icon className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium">{item.label}</span>
              </label>
            ))}
          </div>
          <div className="flex justify-between pt-2 border-t">
            <Button variant="outline" size="sm" onClick={resetNavPrefs} data-testid="nav-prefs-reset">
              Reset to Default
            </Button>
            <Button size="sm" onClick={() => setNavPrefsOpen(false)} data-testid="nav-prefs-done">
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <PrintSettingsDialog 
        open={printSettingsOpen} 
        onOpenChange={setPrintSettingsOpen} 
      />

      {/* Mobile overlay - tap to close sidebar */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen pb-16 lg:pb-0 max-w-full overflow-x-hidden">
        {/* Header - Compact on mobile */}
        <header className="sticky top-0 z-30 bg-background/95 backdrop-blur-md border-b border-border h-14 lg:h-16 flex items-center px-3 lg:px-6">
          {/* Hamburger Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="mr-2 lg:mr-4 hover:bg-muted h-10 w-10 touch-manipulation"
            onClick={toggleSidebar}
            data-testid="menu-toggle"
          >
            <Menu className="w-5 h-5" />
          </Button>
          
          <div className="flex-1 flex items-center gap-2 lg:gap-3 min-w-0">
            {/* Flame icon always visible on mobile for brand recognition */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Flame className="w-5 h-5 lg:w-6 lg:h-6 text-orange-500" />
              <span className="font-heading text-base lg:text-lg font-bold bg-gradient-to-r from-orange-500 via-emerald-500 to-blue-500 bg-clip-text text-transparent">
                EximFlow
              </span>
            </div>
            {/* Hide subtitle on mobile */}
            <h1 className="hidden md:block font-heading text-lg font-semibold text-foreground ml-2 truncate">
              EXIM Business Management
            </h1>
          </div>
          
          {/* Global Search */}
          <div className="hidden sm:flex flex-1 justify-center max-w-md mx-4">
            <GlobalSearch />
          </div>
          
          {/* Mobile search button */}
          <div className="sm:hidden">
            <GlobalSearch />
          </div>
          
          {/* Mobile quick actions */}
          <div className="flex items-center gap-1 lg:hidden">
            {(user?.role === "super_admin" || user?.role === "error_manager") && syncStatus && (
              <div 
                className={`
                  p-2 rounded-full touch-manipulation
                  ${syncStatus?.sync_health === "good" 
                    ? 'text-green-500' 
                    : syncStatus?.sync_health === "critical"
                      ? 'text-red-500'
                      : 'text-yellow-500'
                  }
                `}
                onClick={handleManualSync}
              >
                {syncing ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : syncStatus?.sync_health === "good" ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <AlertCircle className="w-5 h-5" />
                )}
              </div>
            )}
          </div>
        </header>

        {/* Page content - adjusted padding for mobile */}
        <main className="flex-1 p-3 lg:p-6 overflow-auto">
          <Outlet />
          <InstallAppPrompt />
        </main>
        
        {/* Mobile Bottom Navigation Bar */}
        <nav className="fixed bottom-0 left-0 right-0 z-40 bg-background/95 backdrop-blur-md border-t border-border lg:hidden safe-area-bottom">
          <div className="flex items-center justify-around h-16 px-2">
            {mobileNavItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`
                    flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-lg
                    min-w-[64px] touch-manipulation transition-colors
                    ${isActive 
                      ? 'text-orange-500' 
                      : 'text-muted-foreground hover:text-foreground active:text-foreground'}
                  `}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className={`w-5 h-5 ${isActive ? 'scale-110' : ''} transition-transform`} />
                  <span className="text-[10px] font-medium">{item.label}</span>
                </NavLink>
              );
            })}
            
            {/* More menu button */}
            <button
              onClick={() => setMobileMoreOpen(!mobileMoreOpen)}
              className={`
                flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-lg
                min-w-[64px] touch-manipulation transition-colors
                ${mobileMoreOpen 
                  ? 'text-orange-500' 
                  : 'text-muted-foreground hover:text-foreground active:text-foreground'}
              `}
              data-testid="mobile-nav-more"
            >
              <MoreHorizontal className="w-5 h-5" />
              <span className="text-[10px] font-medium">More</span>
            </button>
          </div>
          
          {/* More menu popup */}
          {mobileMoreOpen && (
            <>
              <div 
                className="fixed inset-0 z-30" 
                onClick={() => setMobileMoreOpen(false)}
              />
              <div className="absolute bottom-full left-0 right-0 mb-2 mx-3 p-2 bg-card border border-border rounded-xl shadow-xl z-40 animate-in slide-in-from-bottom-2 duration-200">
                <div className="grid grid-cols-4 gap-2">
                  {navItemsAll
                    .filter(item => !mobileNavItems.some(m => m.path === item.path))
                    .slice(0, 8)
                    .map((item) => (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        onClick={() => setMobileMoreOpen(false)}
                        className={({ isActive }) => `
                          flex flex-col items-center justify-center gap-1.5 p-3 rounded-lg
                          touch-manipulation transition-colors
                          ${isActive 
                            ? 'bg-orange-500/10 text-orange-500' 
                            : 'text-muted-foreground hover:bg-muted active:bg-muted'}
                        `}
                      >
                        <item.icon className="w-5 h-5" />
                        <span className="text-[10px] font-medium text-center leading-tight">{item.label}</span>
                      </NavLink>
                    ))}
                </div>
                
                {/* User section in more menu */}
                <div className="mt-3 pt-3 border-t border-border">
                  <div className="flex items-center justify-between px-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 via-emerald-500 to-blue-500 flex items-center justify-center">
                        <span className="text-white font-semibold text-xs">
                          {user?.username?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium">{user?.display_name || user?.username}</p>
                        <p className="text-[10px] text-muted-foreground">{getRoleDisplay(user?.role)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setMobileMoreOpen(false);
                          setPrintSettingsOpen(true);
                        }}
                        className="h-9 w-9"
                      >
                        <Settings className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setMobileMoreOpen(false);
                          handleLogout();
                        }}
                        className="h-9 w-9 text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <LogOut className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </nav>
      </div>
    </div>
  );
};

export default DashboardLayout;
